// Transient action feedback — the ONE success/failure voice for the app, plus the Gmail-style
// "X deleted — Undo" pattern. A fixed stack (bottom-centre), aria-live so screen readers hear it,
// auto-dismissing. Plain DOM appended to <body> so it survives tina4's imperative view swaps (the
// same reason the invoiceTest source keeps it off the reactive tree). Ported from the invoiceTest
// prototype's notice.js; row-flash lives in shared/listnav.js (flashRow) — NOT duplicated here.
//
// Usage:
//   notice('Saved');                               // ok, auto-dismiss
//   notice('Could not save', 'bad');               // error (role=alert)
//   notice('Draft deleted', 'info', { undoLabel: 'Undo', onUndo: () => restore() });
// Third arg: a number (duration ms) OR { ms?, undoLabel?, onUndo? }. With both undo fields set the
// notice grows an inline Undo button and lingers longer. Callers pass already-translated text.

const MAX_STACK = 4;          // a bulk loop must not flood the screen
const DEFAULT_MS = 3600;
const UNDO_MS = 6500;         // undoable notices linger so the action is actually reversible
const LEAVE_MS = 350;         // must match the .is-leaving transition in aboo.css

function ensureHost() {
  let host = document.querySelector('.notice-stack');
  if (!host) {
    host = document.createElement('div');
    host.className = 'notice-stack';
    host.setAttribute('aria-live', 'polite');
    document.body.append(host);
  }
  return host;
}

const kindClass = kind => kind === 'ok' ? 'is-ok' : kind === 'info' ? 'is-info' : 'is-bad';

// Show a notice. Returns the element (handy for tests / manual dismissal).
export function notice(message, kind = 'ok', optsOrMs) {
  const opts = typeof optsOrMs === 'number' ? { ms: optsOrMs } : (optsOrMs || {});
  const undoable = !!(opts.undoLabel && opts.onUndo);
  const ms = typeof opts.ms === 'number' ? opts.ms : (undoable ? UNDO_MS : DEFAULT_MS);

  const host = ensureHost();
  const el = document.createElement('div');
  el.className = 'app-notice ' + kindClass(kind);
  el.setAttribute('role', kind === 'bad' ? 'alert' : 'status');

  let timer;
  const leave = () => {
    clearTimeout(timer);
    el.classList.add('is-leaving');
    setTimeout(() => el.remove(), LEAVE_MS);
  };

  if (undoable) {
    const msg = document.createElement('span');
    msg.className = 'notice-msg';
    msg.textContent = message;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'notice-undo';
    btn.textContent = opts.undoLabel;
    let fired = false;                        // one shot — a double-click must never undo twice
    btn.addEventListener('click', () => {
      if (fired) return;
      fired = true;
      try { opts.onUndo(); } finally { leave(); }
    });
    el.append(msg, btn);
  } else {
    el.textContent = message;
  }

  host.append(el);
  while (host.children.length > MAX_STACK) host.firstElementChild.remove();
  timer = setTimeout(leave, ms);
  return el;
}
